import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useParams } from 'react-router-dom'

import Loading from '../../components/loading'
import SettingsCard from '../../components/Settings/SettingsCard'
import SettingsLayout from '../../components/Settings/SettingsLayout'
import { ROUTES } from '../../constants/routes'
import useApi from '../../hooks/useApi'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import {
  CHARITY_LOCATION_TYPES,
  charityLocationPayload,
  charityLocationsEqual,
  parseGooglePlaceToCharityLocation,
} from '../../lib/charityLocation'
import {
  charityAccountPath,
  charityPublicProfilePath,
  userManagedCharityRegistrations,
} from '../../lib/charityProfileLink'
import { ensureAbsoluteUrl } from '../../utils/url'
import CharitySettingsContent from './settings/CharitySettingsContent'
import {
  ABOUT_FIELDS,
  DONATION_LINK_FIELDS,
  SOCIAL_LINK_FIELDS,
} from './settings/constants'

function buildInitialLocations(charity) {
  return {
    volunteer: charity?.volunteer_location ?? null,
    donation: charity?.donation_location ?? null,
    visitor: charity?.visitor_location ?? null,
  }
}

function normalizeLinkField(field, raw) {
  if (field.includes('_url') || field.includes('_link')) {
    return ensureAbsoluteUrl(raw) || null
  }
  return raw || null
}

function normalizeAboutField(field, raw) {
  if (field === 'mission') {
    return (raw ?? '').trim() || null
  }
  return normalizeLinkField(field, raw)
}

function fieldsChanged(charity, initial, fieldKeys) {
  if (!charity || !initial) return false
  return fieldKeys.some(
    key =>
      normalizeLinkField(key, charity[key]) !==
      normalizeLinkField(key, initial[key]),
  )
}

function CharitySettingsUnavailable({ message, t }) {
  return (
    <SettingsLayout
      title={t('charities.settings.title')}
      breadcrumbs={[
        { label: t('navigation_banner.home'), to: '/' },
        { label: t('charities.myCharities.title'), to: ROUTES.CHARITIES },
        { label: t('charities.settings.title') },
      ]}
    >
      <SettingsCard>
        <Alert variant="warning">{message}</Alert>
        <Link to={ROUTES.CHARITIES}>
          {t('charities.settings.back_to_charities')}
        </Link>
      </SettingsCard>
    </SettingsLayout>
  )
}

export default function CharityEdit() {
  const { t } = useTranslation()
  const { locale } = useCurrentUser()
  const { id: idParam } = useParams()
  const charityId = Number(idParam)

  const { getCharity, getUserCharityRegistrations, updateCharity } = useApi()

  const [charity, setCharity] = useState(null)
  const [initialCharity, setInitialCharity] = useState(null)
  const [locations, setLocations] = useState({
    volunteer: null,
    donation: null,
    visitor: null,
  })
  const [initialLocations, setInitialLocations] = useState({
    volunteer: null,
    donation: null,
    visitor: null,
  })
  const [clearedLocations, setClearedLocations] = useState({
    volunteer: false,
    donation: false,
    visitor: false,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [accessDenied, setAccessDenied] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [verifiedCharities, setVerifiedCharities] = useState([])

  const stripeReturnPath = charityAccountPath(charityId)

  const fetchCharity = useCallback(async () => {
    if (!charityId || Number.isNaN(charityId)) {
      setNotFound(true)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    setAccessDenied(false)
    setNotFound(false)

    try {
      const [registrationsResponse, charityResponse] = await Promise.all([
        getUserCharityRegistrations(),
        getCharity(charityId),
      ])

      const registrations = userManagedCharityRegistrations(
        registrationsResponse.data,
      )
      setVerifiedCharities(registrations)

      const canEdit = registrations.some(
        reg => Number(reg.charity?.id) === charityId,
      )
      if (!canEdit) {
        setAccessDenied(true)
        setLoading(false)
        return
      }

      if (charityResponse.status === 404 || charityResponse.error) {
        setNotFound(true)
        setLoading(false)
        return
      }

      const data = charityResponse.data
      if (!data) {
        setNotFound(true)
        setLoading(false)
        return
      }

      setCharity(data)
      setInitialCharity(data)
      const nextLocations = buildInitialLocations(data)
      setLocations(nextLocations)
      setInitialLocations(nextLocations)
      setClearedLocations({ volunteer: false, donation: false, visitor: false })
    } catch (_err) {
      setError(t('charities.settings.save_failed_retry'))
    } finally {
      setLoading(false)
    }
  }, [charityId, getCharity, getUserCharityRegistrations, t])

  useEffect(() => {
    fetchCharity()
  }, [fetchCharity])

  const updateCharityField = useCallback((field, value) => {
    setCharity(prev => ({ ...prev, [field]: value }))
  }, [])

  const updateSocialField = useCallback((field, value) => {
    setCharity(prev => ({ ...prev, [field]: value }))
  }, [])

  const handleLocationChange = useCallback((stateKey, place) => {
    const nextLocation = parseGooglePlaceToCharityLocation(place)
    setLocations(prev => ({ ...prev, [stateKey]: nextLocation }))
    setClearedLocations(prev => ({ ...prev, [stateKey]: nextLocation == null }))
  }, [])

  const applySavedCharity = useCallback(data => {
    setCharity(data)
    setInitialCharity(data)
    const nextLocations = buildInitialLocations(data)
    setLocations(nextLocations)
    setInitialLocations(nextLocations)
    setClearedLocations({
      volunteer: false,
      donation: false,
      visitor: false,
    })
  }, [])

  const imageCharityLogoOnChange = useCallback(
    async mediaAttachment => {
      if (!charityId || !mediaAttachment?.id) return

      setSaving(true)
      setError(null)

      try {
        // Mirror business/user avatar updates: attachment id is top-level.
        // Charity PATCH still params.require(:charity), and an empty `{}` is
        // blank in Rails so it 400s — send a non-blank ignored attr.
        const response = await updateCharity(charityId, {
          charity: {
            organization_name: charity?.organization_name,
          },
          logo_media_attachment_id: mediaAttachment.id,
        })
        const status = response.status ?? response.error?.response?.status
        const errorBody = response.error?.response?.data

        if (status === 403) {
          setAccessDenied(true)
          setError(t('charities.settings.access_denied'))
          return
        }

        if (response.errors?.length || (response.error && status !== 200)) {
          setError(
            response.errors?.full_messages?.[0] ||
              (Array.isArray(response.errors)
                ? response.errors.join(', ')
                : null) ||
              errorBody?.error ||
              errorBody?.errors?.full_messages?.[0] ||
              (typeof errorBody === 'string' ? errorBody : null) ||
              t('charities.settings.logo_upload_failed'),
          )
          return
        }

        // Same as user/business: prefer the just-uploaded attachment for UI.
        const previewUrl =
          mediaAttachment.thumbnail_url ??
          mediaAttachment.direct_upload_url ??
          null

        if (response.data) {
          applySavedCharity({
            ...response.data,
            logo_url: previewUrl || response.data.logo_url || null,
          })
        } else if (previewUrl) {
          setCharity(prev => ({ ...prev, logo_url: previewUrl }))
          setInitialCharity(prev => ({ ...prev, logo_url: previewUrl }))
        }
      } catch (_err) {
        setError(t('charities.settings.logo_upload_failed'))
      } finally {
        setSaving(false)
      }
    },
    [
      applySavedCharity,
      charity?.organization_name,
      charityId,
      t,
      updateCharity,
    ],
  )

  const persistCharity = useCallback(
    async payload => {
      if (!charityId || !charity) return false

      setSaving(true)
      setError(null)

      try {
        const response = await updateCharity(charityId, payload)
        const status = response.status ?? response.error?.response?.status

        if (status === 403) {
          setAccessDenied(true)
          setError(t('charities.settings.access_denied'))
          return false
        }

        if (response.errors?.length) {
          setError(
            Array.isArray(response.errors)
              ? response.errors.join(', ')
              : String(response.errors),
          )
          return false
        }

        if (response.error && status !== 200) {
          setError(
            response.error?.response?.data?.error ||
              t('charities.settings.save_failed'),
          )
          return false
        }

        if (response.data) {
          applySavedCharity(response.data)
        }

        return true
      } catch (_err) {
        setError(t('charities.settings.save_failed_retry'))
        return false
      } finally {
        setSaving(false)
      }
    },
    [applySavedCharity, charity, charityId, t, updateCharity],
  )

  const onSaveAbout = useCallback(async () => {
    const charityPayload = {}
    ABOUT_FIELDS.forEach(field => {
      charityPayload[field] = normalizeAboutField(field, charity?.[field])
    })
    await persistCharity({ charity: charityPayload })
  }, [charity, persistCharity])

  const onSaveDonationLinks = useCallback(async () => {
    const charityPayload = {}
    DONATION_LINK_FIELDS.forEach(field => {
      charityPayload[field] = normalizeLinkField(field, charity?.[field])
    })
    await persistCharity({ charity: charityPayload })
  }, [charity, persistCharity])

  const onSaveSocials = useCallback(async () => {
    const charityPayload = {}
    SOCIAL_LINK_FIELDS.forEach(field => {
      charityPayload[field] = normalizeLinkField(field, charity?.[field])
    })
    await persistCharity({ charity: charityPayload })
  }, [charity, persistCharity])

  const onSaveLocations = useCallback(async () => {
    const payload = {}

    CHARITY_LOCATION_TYPES.forEach(({ key, stateKey }) => {
      const current = locations[stateKey]
      const initial = initialLocations[stateKey]

      if (clearedLocations[stateKey] && initial) {
        payload[key] = null
        return
      }

      if (!charityLocationsEqual(current, initial)) {
        if (current) {
          payload[key] = charityLocationPayload(current)
        } else if (initial) {
          payload[key] = null
        }
      }
    })

    // Locations are top-level siblings; charity must be non-blank for require(:charity).
    await persistCharity({
      charity: { organization_name: charity?.organization_name },
      ...payload,
    })
  }, [
    charity?.organization_name,
    clearedLocations,
    initialLocations,
    locations,
    persistCharity,
  ])

  const hasAboutChanged = useMemo(
    () =>
      ABOUT_FIELDS.some(
        field =>
          normalizeAboutField(field, charity?.[field]) !==
          normalizeAboutField(field, initialCharity?.[field]),
      ),
    [charity, initialCharity],
  )

  const hasDonationLinksChanged = useMemo(
    () => fieldsChanged(charity, initialCharity, DONATION_LINK_FIELDS),
    [charity, initialCharity],
  )

  const hasSocialsChanged = useMemo(
    () => fieldsChanged(charity, initialCharity, SOCIAL_LINK_FIELDS),
    [charity, initialCharity],
  )

  const hasLocationsChanged = useMemo(() => {
    return CHARITY_LOCATION_TYPES.some(({ stateKey }) => {
      if (clearedLocations[stateKey] && initialLocations[stateKey]) return true
      return !charityLocationsEqual(
        locations[stateKey],
        initialLocations[stateKey],
      )
    })
  }, [clearedLocations, initialLocations, locations])

  const breadcrumbs = useMemo(() => {
    if (!charity) return []
    const profilePath = charityPublicProfilePath(charity.id)
    return [
      { label: t('navigation_banner.home'), to: '/' },
      {
        label:
          charity.organization_name || t('navigation_banner.charity_profile'),
        to: profilePath || undefined,
      },
      { label: t('charities.settings.title') },
    ]
  }, [charity, t])

  if (Number.isNaN(charityId)) {
    return <Navigate to={ROUTES.CHARITY_ACCOUNT} replace />
  }

  if (loading) {
    return <Loading show />
  }

  if (accessDenied) {
    return (
      <CharitySettingsUnavailable
        message={t('charities.settings.access_denied')}
        t={t}
      />
    )
  }

  if (notFound) {
    return (
      <CharitySettingsUnavailable
        message={t('charities.settings.not_found')}
        t={t}
      />
    )
  }

  if (!charity) {
    return <Loading show />
  }

  return (
    <>
      {saving && <Loading show />}
      <CharitySettingsContent
        charity={charity}
        charityId={charityId}
        breadcrumbs={breadcrumbs}
        error={error}
        setError={setError}
        setCharity={setCharity}
        locale={locale}
        updateCharity={updateCharity}
        verifiedCharities={verifiedCharities}
        locations={locations}
        clearedLocations={clearedLocations}
        onLocationChange={handleLocationChange}
        updateCharityField={updateCharityField}
        updateSocialField={updateSocialField}
        onLogoChange={imageCharityLogoOnChange}
        onSaveAbout={onSaveAbout}
        onSaveDonationLinks={onSaveDonationLinks}
        onSaveSocials={onSaveSocials}
        onSaveLocations={onSaveLocations}
        hasAboutChanged={hasAboutChanged}
        hasDonationLinksChanged={hasDonationLinksChanged}
        hasSocialsChanged={hasSocialsChanged}
        hasLocationsChanged={hasLocationsChanged}
        saving={saving}
        stripeReturnPath={stripeReturnPath}
        t={t}
      />
    </>
  )
}
